/**
 * Retiro en otra sucursal (Fase 4 de retiros; respuestas de la dueña y de la contadora del 2026-10-29, migración 083).
 *
 * El cliente compra en una sucursal y retira en otra. En la sucursal de venta se hace la «orden de retiro» sobre la
 * factura o nota (autorizada por la dueña, por quien tenga su permiso delegado o con la autorización de siempre): lo
 * apartado pasa a la sucursal de retiro y el cliente recibe un código de 6 dígitos. En la otra tienda el despachador
 * pide el código y la cédula y registra el retiro (puede ser en partes): una guía de traslado entre sucursales al costo,
 * sin factura, y la entrega contra la factura o nota de la sucursal de venta. Vale 7 días, se extiende con justificación
 * y se anula con motivo; al vencer o anularse, lo apartado vuelve a la sucursal de venta.
 */
import { z } from 'zod';
import { decimalPositivo, paginacion, uuid } from './comun.ts';
import type { CorreoAutomaticoDTO } from './correo.ts';
import type { EntregaDTO } from './inventario.ts';

export const ESTADOS_ORDEN_RETIRO = ['activa', 'parcial', 'retirada', 'vencida', 'anulada'] as const;
export type EstadoOrdenRetiro = (typeof ESTADOS_ORDEN_RETIRO)[number];
export const ESTADO_ORDEN_RETIRO_ETIQUETA: Record<EstadoOrdenRetiro, string> = { activa: 'Activa', parcial: 'Parcial', retirada: 'Retirada', vencida: 'Vencida', anulada: 'Anulada' };

/** Días que vale la orden desde que se crea, y los que suma cada extensión (respuesta de la dueña: «7 días, extensible»). */
export const DIAS_ORDEN_RETIRO = 7;
/** Intentos fallidos del código (o de la cédula) antes de bloquearlo: la sucursal de venta genera uno nuevo. */
export const INTENTOS_CODIGO_RETIRO = 5;

/** El código que da el cliente: 6 dígitos. */
export const codigoRetiro = z.string().trim().regex(/^\d{6}$/, 'El código de retiro tiene 6 dígitos');
const lineasRetiro = z.array(z.object({ documento_linea_id: uuid, cantidad: decimalPositivo })).min(1, 'Indica qué se retira').max(200);
const autorizaciones = z.array(uuid).max(10).optional();

/** Crear la orden (sucursal de venta). Quién retirará es opcional; si se indica la cédula, el retiro la exige. */
export const ordenRetiroInput = z
  .object({
    almacen_retiro_id: uuid,
    lineas: lineasRetiro,
    retira_nombre: z.string().trim().max(120).optional(),
    // Se compara por sus dígitos: sin ellos cualquier cédula pasaría (revisión de base de datos)
    retira_cedula: z.string().trim().max(40).refine((v) => (v.match(/\d/g)?.length ?? 0) >= 5, 'La cédula de quien retira lleva al menos 5 dígitos').optional(),
    autorizaciones,
  })
  .strict();
export type OrdenRetiroInput = z.infer<typeof ordenRetiroInput>;

/** Registrar el retiro (sucursal de retiro): el código y la cédula de quien retira, y lo que se lleva ahora. */
export const retiroOtraSucursalInput = z
  .object({
    codigo: codigoRetiro,
    cedula: z.string().trim().min(3, 'Escribe la cédula de quien retira').max(40),
    recibe_nombre: z.string().trim().max(120).optional(),
    observaciones: z.string().trim().max(500).optional(),
    lineas: lineasRetiro,
    autorizaciones,
  })
  .strict();
export type RetiroOtraSucursalInput = z.infer<typeof retiroOtraSucursalInput>;

export const extenderOrdenRetiroInput = z.object({ justificacion: z.string().trim().min(5, 'Justifica la extensión (mínimo 5 caracteres)').max(500), autorizaciones }).strict();
export type ExtenderOrdenRetiroInput = z.infer<typeof extenderOrdenRetiroInput>;
export const anularOrdenRetiroInput = z.object({ motivo: z.string().trim().min(5, 'Escribe el motivo (mínimo 5 caracteres)').max(500), autorizaciones }).strict();
export type AnularOrdenRetiroInput = z.infer<typeof anularOrdenRetiroInput>;
export const nuevoCodigoRetiroInput = z.object({ autorizaciones }).strict();
export type NuevoCodigoRetiroInput = z.infer<typeof nuevoCodigoRetiroInput>;

/** Lista: las de mi sucursal como venta (`venta`), como retiro (`retiro`) o las dos; solo las abiertas con `abiertas`. */
export const ordenesRetiroFiltros = paginacion.extend({
  ver: z.enum(['venta', 'retiro']).optional(),
  estado: z.enum(ESTADOS_ORDEN_RETIRO).optional(),
  abiertas: z.enum(['true', 'false']).optional().transform((v) => v === 'true'),
  q: z.string().trim().max(80).optional(),
});
export type OrdenesRetiroFiltros = z.infer<typeof ordenesRetiroFiltros>;

export interface OrdenRetiroLineaDTO {
  documento_linea_id: string;
  producto_id: string;
  producto_codigo: string;
  producto_nombre: string;
  unidad: string;
  /** Donde estaba apartado en la sucursal de venta (allí vuelve si vence o se anula). */
  almacen_origen_nombre: string;
  cantidad: string;
  retirado: string;
  liberado: string;
  /** Lo que falta por retirar con esta orden (0 si está cerrada). */
  pendiente: string;
}

export interface OrdenRetiroDTO {
  id: string;
  numero_formateado: string;
  estado: EstadoOrdenRetiro;
  /** Abierta y pasada de su fecha, todavía sin procesar (lo apartado vuelve al abrir Entregas o con el reloj). */
  vencida_sin_procesar: boolean;
  documento_id: string;
  documento_tipo: 'FAC' | 'NOTA';
  documento_numero: string;
  cliente_razon_social: string;
  cliente_rif: string;
  sucursal_id: string;
  sucursal_nombre: string;
  sucursal_retiro_id: string;
  sucursal_retiro_nombre: string;
  almacen_retiro_id: string;
  almacen_retiro_nombre: string;
  /** Dirección del almacén de retiro (o de la tienda si el almacén no tiene). */
  direccion_retiro: string;
  vence_en: string;
  creado_en: string;
  creado_por_nombre: string;
  autorizado_por_nombre: string | null;
  retira_nombre: string | null;
  retira_cedula: string | null;
  codigo_bloqueado: boolean;
  codigo_intentos_restantes: number;
  extensiones: { en: string; por_nombre: string; justificacion: string; vence_antes: string; vence_despues: string }[];
  cerrada_en: string | null;
  anulada_por_nombre: string | null;
  motivo_anulacion: string | null;
  /** De qué lado está quien la ve: la sucursal de venta la gestiona; la de retiro registra el retiro. */
  lado: 'venta' | 'retiro' | 'global';
  puede_retirar: boolean;
  puede_gestionar: boolean;
  lineas: OrdenRetiroLineaDTO[];
  /** Cada retiro: la entrega (de la sucursal de venta) y su guía de traslado (de la sucursal de retiro). */
  retiros: { entrega_id: string; entrega_numero: string; traslado_id: string; traslado_numero: string; entregado_en: string; usuario_nombre: string; recibe_nombre: string | null; recibe_documento: string | null }[];
}

/** Al crear la orden o generar un código nuevo: el código se muestra UNA vez (después solo existe su hash). */
export interface OrdenRetiroCreadaDTO extends OrdenRetiroDTO {
  /** null si la respuesta es la repetición de una operación ya hecha (la misma clave): el código ya se mostró. */
  codigo: string | null;
  correo_orden: CorreoAutomaticoDTO;
}

/** Respuesta del retiro en la otra sucursal: la entrega, su guía de traslado y cómo quedó la orden. */
export type RetiroOtraSucursalDTO = EntregaDTO & {
  orden: OrdenRetiroDTO;
  /** Correo «Estado de su retiro» al cliente. */
  correo_retiro: CorreoAutomaticoDTO;
  /** Si quedó algo por retirar: el código nuevo va solo al correo del cliente (aquí no se muestra). */
  correo_codigo: CorreoAutomaticoDTO | null;
};

/** Para el diálogo de la orden: lo que se puede pedir por renglón y los almacenes de las otras sucursales con su disponible. */
export interface RetiroOtraSucursalOpcionesDTO {
  documento_id: string;
  documento_tipo: 'FAC' | 'NOTA';
  documento_numero: string;
  cliente_razon_social: string;
  /** La orden abierta de esta venta, si ya hay una (solo una a la vez). */
  orden_abierta: { id: string; numero_formateado: string; sucursal_retiro_nombre: string } | null;
  lineas: { documento_linea_id: string; producto_id: string; producto_codigo: string; producto_nombre: string; unidad: string; pendiente: string; sin_inventario: boolean; almacen_nombre: string | null }[];
  almacenes: { id: string; nombre: string; sucursal_id: string; sucursal_nombre: string; direccion: string; disponibles: Record<string, string> }[];
}
